import { optimizedApi } from '@/utils/api/optimized-api';

export interface ClaimsReportSummary {
  total: number;
  pending: number;
  approved: number;
  declined: number;
  approval_rate: number;
}

export interface ClaimsReportAgent {
  agent_name: string;
  total: number;
  pending: number;
  approved: number;
  declined: number;
  approval_rate: number;
}

export interface ClaimsReportDay {
  day: string;
  total: number;
  pending: number;
  approved: number;
  declined: number;
}

export interface ClaimsReportApprover {
  approver_name: string;
  total: number;
  approved: number;
  declined: number;
}

export interface ClaimsReport {
  summary: ClaimsReportSummary;
  by_agent: ClaimsReportAgent[];
  by_day: ClaimsReportDay[];
  by_approver: ClaimsReportApprover[];
  from_date: string;
  to_date: string;
}

export interface LeadClaim {
  uuid: string;
  lead_uuid: string;
  lead_name: string | null;
  lead_phone: string | null;
  claimed_by_user_name: string | null;
  current_owner_name: string | null;
  reason: string | null;
  status: 'pending' | 'approved' | 'declined';
  admin_note: string | null;
  actioned_by_user_name: string | null;
  created_at: string;
  actioned_at: string | null;
  disposition_l1_label: string | null;
  disposition_l2_label: string | null;
  disposition_l3_label: string | null;
}

export interface InAppNotification {
  uuid: string;
  type: string;
  title: string;
  body: string | null;
  lead_uuid: string | null;
  claim_uuid: string | null;
  is_read: boolean;
  created_at: string;
}

export const claimsService = {
  submitClaim: (lead_uuid: string, reason?: string) =>
    optimizedApi.post('/lead-claims', { lead_uuid, reason: reason || null }),

  getPendingClaims: () =>
    optimizedApi.get<LeadClaim[]>('/lead-claims/pending'),

  getAllClaims: (from_date?: string, to_date?: string) => {
    const params = new URLSearchParams();
    if (from_date) params.set('from_date', from_date);
    if (to_date) params.set('to_date', to_date);
    const qs = params.toString();
    return optimizedApi.get<LeadClaim[]>(`/lead-claims/all${qs ? `?${qs}` : ''}`);
  },

  getMyClaims: () =>
    optimizedApi.get<LeadClaim[]>('/lead-claims/my'),

  getPendingCount: () =>
    optimizedApi.get<{ count: number }>('/lead-claims/pending-count'),

  approve: (claim_uuid: string, admin_note?: string) =>
    optimizedApi.put(`/lead-claims/${claim_uuid}/approve`, { admin_note: admin_note || null }),

  decline: (claim_uuid: string, admin_note?: string) =>
    optimizedApi.put(`/lead-claims/${claim_uuid}/decline`, { admin_note: admin_note || null }),

  getReports: (from_date?: string, to_date?: string) => {
    const params = new URLSearchParams();
    if (from_date) params.set('from_date', from_date);
    if (to_date) params.set('to_date', to_date);
    const qs = params.toString();
    return optimizedApi.get<ClaimsReport>(`/lead-claims/reports${qs ? `?${qs}` : ''}`);
  },
};

export const notificationsService = {
  getUnreadCount: () =>
    optimizedApi.get<{ count: number }>('/notifications/unread-count'),

  list: () =>
    optimizedApi.get<InAppNotification[]>('/notifications'),

  markRead: (uuid: string) =>
    optimizedApi.put(`/notifications/${uuid}/read`, {}),

  markAllRead: () =>
    optimizedApi.put('/notifications/mark-all-read', {}),
};
